import React, { useState, useEffect } from 'react';
import CustomTable from '@components/Common/table/CustomTable';
import { useAllAgentsQuery } from '../API/Agents';
import useDebounce from '@hooks/useDebounce';
import { defaultParams } from '@utils/global.constants';
import { useModalDisclosure } from '@hooks/useDisclosure';
import AgentView from '../pages/Agents/AgentView';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';

const TableHeadCells = [
  // { id: 'id', label: 'ID', nested: 'id' },
  { id: 'name', label: 'Name', nested: 'name' },
  { id: 'createdAt', label: 'Created At', nested: 'createdAt', type: 'dateTime' },
  // { id: 'firstMessage', label: 'First Message', nested: 'firstMessage' },
  { id: 'model', label: 'Model', nested: 'model' },
  { id: 'isAllowInbound', label: 'Allow Inbound', nested: 'isAllowInbound', type: 'boolean' },
  { id: 'isAllowOutbound', label: 'Allow Outbound', nested: 'isAllowOutbound', type: 'boolean' },
  { id: 'isSendEmail', label: 'Send Email', nested: 'isSendEmail', type: 'boolean' },
  { id: 'bookAppointment', label: 'Book Appointment', nested: 'bookAppointment', type: 'boolean' },
  { id: 'actions', label: 'Actions' },
];

const TableDataFields = [
  // 'id',
  'name',
  'createdAt',
  // 'firstMessage',
  'model',
  'isAllowInbound',
  'isAllowOutbound',
  'isSendEmail',
  'bookAppointment',
  'actions',
];

function AgentList() {
  const [params, setParams] = useState(defaultParams);
  const debouncedSearch = useDebounce(params.search, 500);
  const { isOpen, close, open, data: selectedAgent } = useModalDisclosure();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract userId and userName from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const userId = searchParams.get('userId');
  const userName = searchParams.get('userName');
  
  const { isLoading, isFetching, data } = useAllAgentsQuery({
    params: { 
      page: params.page,
      limit: params.pageSize,
      sortBy: params.sortKey,
      sortOrder: params.sortOrder,
      search: debouncedSearch,
      userId: userId
    },
    config: { keepPreviousData: true },
  });

  console.log('AgentList data:', data);

  // Flatten nested fields for table display
  // Since axios interceptor already returns response.data, 'data' here is the actual response
  const rawData = Array.isArray(data) ? data : (data?.data || []);
  const tableData = rawData.map((agent: any) => ({
    ...agent,
    model: agent.model?.model || agent.model || '',
    isAllowInbound: agent.isAllowInbound ?? agent.metadata?.isAllowInbound ?? false,
    isAllowOutbound: agent.isAllowOutbound ?? agent.metadata?.isAllowOutbound ?? false,
    isSendEmail: agent.isSendEmail ?? agent.metadata?.isSendEmail ?? false,
    bookAppointment: agent.bookAppointment ?? agent.metadata?.bookAppointment ?? false,
    prompt : agent.model?.messages?.[0]?.content || '',
    isVerified: true, // Set to true to hide verify button
    phoneNumber: agent.phoneNumbers?.[0]?.phoneNumber || '',
  }));

  const handleParamsChange = (key: string, value: string | number) => {
    setParams({ ...params, [key]: value });
  };

  const handleTableActions = ({ action, data }: any) => {
    switch (action) {
      case 'view':
        open(data);
        break;
      case 'edit':
        // Handle edit action if needed
        console.log('Edit action for:', data);
        break;
      case 'delete':
        // Handle delete action if needed
        console.log('Delete action for:', data);
        break;
      default:
        break;
    }
  };

  return (
    <div className="main-dashboard-div">
      <div className="dashboard-wrapper">
        <div className="heading-dashboard">
          {userId && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div
                onClick={() => navigate('/app/users')}
                style={{
                  cursor: 'pointer',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  backgroundColor: 'transparent',
                  border: '1px solid #d1d5db',
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  const target = e.target as HTMLElement;
                  target.style.backgroundColor = '#f9fafb';
                  target.style.borderColor = '#9ca3af';
                  target.style.transform = 'translateY(-1px)';
                  target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  const target = e.target as HTMLElement;
                  target.style.backgroundColor = 'transparent';
                  target.style.borderColor = '#d1d5db';
                  target.style.transform = 'translateY(0)';
                  target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                }}
                title="Back to Users"
              >
                <ArrowBack fontSize="small" />
                <span>Back to Users</span>
              </div>
              <div style={{
                padding: '6px 12px',
                backgroundColor: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '6px',
                color: '#92400e',
                fontSize: '13px',
                fontWeight: '500'
              }}>
                Filtering for: {userName ? decodeURIComponent(userName) : `User ID: ${userId}`}
              </div>
            </div>
          )}
        </div>
        <div className="main-heading-div">
          <h2>Agent List</h2>
        </div>
        
        {/* Search Section */}
        <div style={{ 
          marginBottom: '20px',
          padding: '16px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px'
        }}>
          <div style={{ maxWidth: '400px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Search Agents
            </label>
            <input
              type="text"
              placeholder="Search by name, VAPI ID, first message, or end call message..."
              value={params.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleParamsChange('search', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>
        </div>
        
        <div>
          {(isLoading || isFetching) ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
          ) : tableData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>No data found.</div>
          ) : (
            <CustomTable
              tableData={tableData}
              headCells={TableHeadCells}
              dataFields={TableDataFields}
              canView={true}
              canEdit={false}
              canDelete={false}
              loading={isLoading}
              fetching={isFetching}
              totalPages={data?.pagination?.totalPages || Math.ceil((data?.meta?.total || data?.total || rawData.length) / params.pageSize) || 1}
              rowsPerPage={data?.pagination?.itemsPerPage || params.pageSize}
              currentPage={data?.pagination?.currentPage || params.page}
              handlePageChange={(page) => page !== (data?.pagination?.currentPage || data?.meta?.currentPage || params.page) && handleParamsChange('page', page)}
              handleSort={(sortKeyOrder) => setParams({ ...params, ...sortKeyOrder, page: 1 })}
              selectedUserAction={handleTableActions}
            />
          )}
        </div>
      </div>
      {isOpen && (
        <AgentView
          isOpen={isOpen}
          data={selectedAgent}
          title="View Agent Details"
          onClose={close}
        />
      )}
    </div>
  );
}

export default AgentList;
